import React, { lazy, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import {Signup, Login,} from './Pages';
import NewSignup from "./Pages/Register/NewSignup";
import ForgotPassword from "./Pages/ForgotPassword";
import ResetPassword from "./Pages/ForgotPassword/ResetPassword";

import { Dashboard, NewLogin, ProjectDashboard } from "./Pages/index";
import RenderLibrary from "./Pages/ProjectV2/Render/RenderLibrary/RenderLibrary";

import { logOutBroadCast } from "./Pages/utils/global";

import AccountSettings from "./Pages/Account/AccountSettings";
import Contactus from "./Pages/Contact us/Contactus";
import PrivacyPolicy from "./Pages/Privacy/PrivacyPolicy";
import Term_and_condition from "./Pages/Terms & Conditions/Term_and_condition";

import HelpingPageLayout from "./Pages/HelpingPage/HelpingPageLayout";
import FontNewUser from "./Pages/NewUser/NewUserComponent/FontNewUser";
import LayoutNewUser from "./Pages/NewUser/NewUserComponent/LayoutNewUser";

import AllPlansList from "./Pages/UserPlan/AllPlansList";
import PaymentsDetails from "./Pages/UserPlan/PaymentsDetails";
import MoreInfoPage from "./Pages/ProjectV2/Render/MoreInfoPage";
import RenderDownloadUnavailable from "./Pages/RenderDownloadUnavailable/RenderDownloadUnavailable";
import OnSubscriptionPlanList from "./Pages/UserPlan/OnSubscriptionPlanList";
import EmbeddedCheckoutPage from "./Pages/UserPlan/EmbeddedCheckoutPage";
import SubscriptionSuccess from "./Pages/UserPlan/SubscriptionSuccess";
import Success from "./Pages/UserPlan/Success";

import { useDispatch, useSelector } from "react-redux";
import { clearAuthStorage, getToken, getTimeUntilTokenExpiry, isAuthenticated, isTokenExpired,} from "./utils/auth";
import { handleSessionExpired, getCurrentPlan, getUserAllInformation, hasUserFilledSurvey } from "./Redux/actions/commonActtion";

// Temporary for newDesign Component
// import Dashboard from "./Pages/Dashboard/June2026/Dashboard";
import ProjectLayout from "./Pages/Projects/ProjectLayout";
import ScrollRender from "./Pages/ProjectV2/Render/June2026/scroll/ScrollRender";
import CardRender from "./Pages/ProjectV2/Render/June2026/card/CardRender";
import MyAllRenders from './Pages/ProjectV2/Render/June2026/MyAllRenders'


const SurveyForm = lazy(() => import("./Pages/NewUser/SurveyForm"));
const MAX_TIMEOUT_MS = 2147483647;

const hasUserFilled = () =>
  JSON.parse(localStorage.getItem("userFilledSurvey"));

function useSessionGuard() {
  const dispatch = useDispatch();
  const [authenticated, setAuthenticated] = React.useState(() =>
    isAuthenticated(),
  );

  React.useEffect(() => {
    let sessionExpiryTimeoutId = null;

    const clearExpiryTimer = () => {
      if (sessionExpiryTimeoutId !== null) {
        window.clearTimeout(sessionExpiryTimeoutId);
        sessionExpiryTimeoutId = null;
      }
    };

    const expireSession = () => {
      clearExpiryTimer();
      clearAuthStorage();
      dispatch(handleSessionExpired());
      setAuthenticated(false);
    };

    const syncSessionState = () => {
      const tokenInStorage = localStorage.getItem("authToken");
      const token = getToken();

      if (!tokenInStorage) {
        clearExpiryTimer();
        setAuthenticated(false);
        return;
      }

      if (!token) {
        expireSession();
        return;
      }

      if (isTokenExpired(token)) {
        expireSession();
        return;
      }

      setAuthenticated(true);
      clearExpiryTimer();

      const sessionRemainingMs = getTimeUntilTokenExpiry(token);
      if (sessionRemainingMs <= 0) {
        expireSession();
        return;
      }

      const timeoutDelay = Math.min(sessionRemainingMs, MAX_TIMEOUT_MS);
      sessionExpiryTimeoutId = window.setTimeout(
        sessionRemainingMs > MAX_TIMEOUT_MS ? syncSessionState : expireSession,
        timeoutDelay,
      );
    };

    const handleStorageChange = (event) => {
      if (!event.key || event.key === "authToken") {
        syncSessionState();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncSessionState();
      }
    };

    syncSessionState();
    window.addEventListener("storage", handleStorageChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearExpiryTimer();
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  return authenticated;
}

function PrivateOutlet() {
  const authenticated = useSessionGuard();
  const location = useLocation();
  const navigate = useNavigate();
  const [surveyGate, setSurveyGate] = useState("pending");

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    hasUserFilledSurvey().then((filled) => {
      if (cancelled) return;
      const done = filled === true;
      localStorage.setItem("userFilledSurvey", JSON.stringify(done));
      setSurveyGate(done ? "ok" : "need");
    });
    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  useEffect(() => {
    if (surveyGate !== "need") return;
    if (hasUserFilled() === true) {
      setSurveyGate("ok");
      return;
    }
    if (location.pathname === "/new_user_survey") return;
    navigate("/new_user_survey", { replace: true });
  }, [surveyGate, location.pathname, navigate]);

  if (!authenticated) return <Navigate to="/login" replace />;
  if (surveyGate === "pending") return null;
  if (surveyGate === "need" && location.pathname !== "/new_user_survey") return null;
  return <Outlet />;
}

function PublicOutlet() {
  const authenticated = useSessionGuard();
  return !authenticated ? <Outlet /> : <Navigate to="/dashboard" replace />;
}

function SurveyOutlet() {
  return hasUserFilled() === true ? <Navigate to="/dashboard" /> : <Outlet />;
}

function ProtectedCardRoute() {
  const dispatch = useDispatch();
  const isCardAdded = useSelector(
    (state) => state?.commonReducer?.userData?.[0]?.isCardAdded,
  );
  const isExpired = useSelector(
    (state) => state?.commonReducer?.currentPlan?.isExpired,
  );
  const userData = useSelector((state) => state?.commonReducer?.userData);
  const currentPlan = useSelector((state) => state?.commonReducer?.currentPlan);

  const userLoaded = Array.isArray(userData) && userData.length > 0;
  const planLoaded = currentPlan && Object.keys(currentPlan).length > 0;

  React.useEffect(() => {
    if (!userLoaded) dispatch(getUserAllInformation());
    if (!planLoaded) dispatch(getCurrentPlan());
  }, [dispatch, userLoaded, planLoaded]);

  if (!userLoaded || !planLoaded) {
    return null;
  }

  return isCardAdded && !isExpired ? (
    <Outlet />
  ) : (
    <Navigate to="/plans" replace />
  );
}

export default function Router() {
  const location = useLocation();

  React.useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "pageview",
      page: location.pathname,
    });
  }, [location]);
  React.useEffect(() => {
    logOutBroadCast();
  }, []);

  return (
    <div className="DIvRoute w-full h-full">
      <Routes>
        <Route
          exact
          path="/render-download-unavailable"
          element={<RenderDownloadUnavailable />}
        />

        {/* ✅ Private Routes */}
        <Route path="/" element={<PrivateOutlet />}>
          {/* ---------------- Dashboard (Protected by card) ---------------- */}
          <Route element={<ProtectedCardRoute />}>
            <Route exact path="dashboard" element={<Dashboard />} />
            <Route exact path="dashboard/myrenders" element={<RenderLibrary  />} />
            {/* Keep editor and render on the same route instance. Switching to
                /render must not remount Project and replace unsaved Redux state
                with an older server snapshot. */}
            <Route path="dashboard/card/:projectId/*" element={<ProjectDashboard />} />
            <Route path="dashboard/scroll/:projectId/*" element={<ProjectDashboard />} />
            <Route path="/dashboard/new-user" element={<Dashboard />} />
            <Route exact path="/dashboard/projectInstructions/LayoutNewUser" element={<LayoutNewUser />} />
            <Route exact path="/dashboard/projectInstructions/FontNewUser" element={<FontNewUser />} />
          </Route>

          {/* ---------------- Non-card protected routes ---------------- */}
          <Route exact path="account-settings" element={<AccountSettings />} />
          <Route path="/more_info" element={<MoreInfoPage />} />
          <Route exact path="user-plans" element={<AllPlansList />} />
          <Route exact path="plans" element={<OnSubscriptionPlanList />} />
          <Route
            exact
            path="subscription-success"
            element={<SubscriptionSuccess />}
          />
          <Route
            exact
            path="user-plan/plan&payment-details"
            element={<PaymentsDetails />}
          />
          <Route exact path="checkout" element={<EmbeddedCheckoutPage />} />
          <Route exact path="subscription-success" element={<SubscriptionSuccess />} />
          <Route exact path="user-plan/plan&payment-details" element={<PaymentsDetails />} />
          <Route path="/paymentsuccess" element={<Success />} />
          <Route path="/help/" element={<HelpingPageLayout />} />
          <Route path="/help/:id" element={<HelpingPageLayout />} />

          {/* Survey */}
          <Route path="/" element={<SurveyOutlet />}>
            <Route path="/new_user_survey" element={<SurveyForm />} />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="dashboard" />} />
          <Route path="*" element={<Navigate to="dashboard" />} />
        </Route>

        {/* ✅ Public Routes */}
        <Route path="/" element={<PublicOutlet />}>
          {/* <Route exact index path="/" element={<NewLogin />} /> */}
          {/* <Route exact index path="/login" element={<NewLogin />} /> */}
          {/* <Route exact path="/register" element={<NewSignup />} /> */}
          {/* <Route exact path="/register" element={<NewSignup />} /> */}
          {/* June2026 New Signup & Login Design */}
          <Route exact index path="/" element={<Login />} />
          <Route exact index path="/login" element={<Login />} />
          <Route exact path="/register" element={<Signup />} />



          <Route exact path="/register/terms" element={<Term_and_condition />} />
          <Route exact path="/contactus" element={<Contactus />} />
          {/* <Route exact path="/forgotpassword" element={<ForgotPassword />} /> */}
          <Route exact path="/forgotpassword" element={<ResetPassword />} />
          <Route exact path="/PrivacyPolicy" element={<PrivacyPolicy />} />
          <Route path="*" element={<Navigate to="/login" />} />

           {/* Temporary for make new deign with new route */}
          <Route path="/new" element={<ProjectLayout/>} />
          {/* <Route exact path="/scroll-render" element={<ScrollRender/>} /> */}
          {/* <Route exact path="/card-render" element={<CardRender/>} /> */}
        </Route>
      </Routes>
    </div>
  );
}
